import React from "react";
import { Stack, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import { createAccount, updateAccount } from "../redux/actions/accountActions";
import { cleanFormData } from "../helpers/formHelpers";

import { AccountType } from "../shared/enums/AccountType";
import { AccountResponseDto } from "../shared/dtos/account/AccountResponseDto";
import { accountActions } from "../redux/slices/account";

const ACCOUNT_TYPES = Object.values(AccountType).map((t) => ({
  value: t,
  label: t,
}));

interface AccountFormValues {
  name: string;
  type: AccountType;
  balance: number | "";
  description: string | "";
}

const getAccountSchema = (
  isEditing: boolean,
): Yup.ObjectSchema<AccountFormValues> =>
  Yup.object().shape({
    name: Yup.string().required("Account name is required"),
    type: Yup.string()
      .oneOf(Object.values(AccountType))
      .required("Account type is required"),
    balance: isEditing
      ? Yup.number()
      : Yup.number().required("Initial balance is required"),
    description: Yup.string().nullable(),
  }) as Yup.ObjectSchema<AccountFormValues>;

interface CreateOrUpdateAccountFormProps {
  account?: AccountResponseDto | null;
  isEditing?: boolean;
  loading: boolean;
  error: string | null;
}

const CreateOrUpdateAccountForm: React.FC<CreateOrUpdateAccountFormProps> = ({
  account = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const initialValues = {
    name: account?.name || "",
    type: account?.type || ("" as AccountType),
    balance: account?.balance || 0,
    description: account?.description || "",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(accountActions.setErrorUpdate(null))
            : () => dispatch(accountActions.setErrorCreate(null))
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={getAccountSchema(isEditing)}
        onSubmit={(values) => {
          const cleanedValues = cleanFormData(values);
          if (isEditing && account) {
            dispatch(updateAccount({ id: account.id, values: cleanedValues }));
          } else {
            dispatch(createAccount(cleanedValues));
          }
        }}
      >
        {({ dirty, isValid }: FormikProps<typeof initialValues>) => (
          <Form>
            <Stack spacing={3}>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                }}
              >
                <FormTextField name="name" label="Account Name" />
                <FormSelectField
                  name="type"
                  label="Account Type"
                  options={ACCOUNT_TYPES}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                }}
              >
                <FormTextField
                  name="balance"
                  label="Initial Balance"
                  type="number"
                  disabled={isEditing}
                  helperText={
                    isEditing ? "Balance cannot be modified directly" : ""
                  }
                />
                <FormTextField
                  name="description"
                  label="Description"
                  multiline
                  rows={2}
                />
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Account"}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                fullWidth
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
export default CreateOrUpdateAccountForm;
